import { useCallback, useEffect, useRef, useState } from "react"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { MULTI_PATIENT_ENABLED, MULTI_PATIENT_START_NEXT_PATIENT_ENABLED } from "constants/featureFlags"
import { useMultiPatientRefreshInterval } from "./useMultiPatientRefreshInterval"
import { dataService } from "utils/dataService"
import { logger } from "utils/logger"

const FINALIZED = "FINALIZED"
const OPEN = "OPEN"

// The API discriminates on `state`; the encounter subscription delivers raw rows that carry an id
// but no state, so those are read as open. A delivery with neither is treated as "nothing arrived"
// and leaves the current encounter alone.
const toEncounterState = (encounter) => {
  if (!encounter) return null
  return encounter.state ?? (encounter.id ? OPEN : null)
}

export const useCurrentEncounter = ({ interactionId, listeningIn = false }) => {
  const [currentEncounterId, setCurrentEncounterId] = useState(null)
  const [currentEncounterSequenceNumber, setCurrentEncounterSequenceNumber] = useState(null)
  const [currentEncounterStatus, setCurrentEncounterStatus] = useState(null)
  const [encounterState, setEncounterState] = useState(null)
  const [loading, setLoading] = useState(true)

  const multiPatientEnabled = useConfigFlag(MULTI_PATIENT_ENABLED)
  const startNextPatientEnabled = useConfigFlag(MULTI_PATIENT_START_NEXT_PATIENT_ENABLED)
  const refreshIntervalSeconds = useMultiPatientRefreshInterval({ listeningIn })

  const latestSequence = useRef(0)
  const loadedInteractionIdRef = useRef(null)

  const applyOpenEncounter = useCallback((encounter) => {
    const incoming = encounter.sequenceNumber ?? 0
    if (incoming && incoming < latestSequence.current) return
    latestSequence.current = incoming
    setEncounterState(OPEN)
    setCurrentEncounterId(encounter.id)
    setCurrentEncounterSequenceNumber(encounter.sequenceNumber ?? null)
    setCurrentEncounterStatus(encounter.status ?? null)
  }, [])

  const applyInactiveEncounter = useCallback((encounter, state) => {
    const incoming = encounter?.sequenceNumber ?? 0
    if (incoming && incoming < latestSequence.current) return
    if (incoming) latestSequence.current = incoming
    setEncounterState(state)
    setCurrentEncounterId(null)
    setCurrentEncounterSequenceNumber(encounter?.sequenceNumber ?? null)
    setCurrentEncounterStatus(encounter?.status ?? null)
  }, [])

  const applyEncounter = useCallback(
    (encounter) => {
      const state = toEncounterState(encounter)
      if (!state) return
      if (state === OPEN) {
        applyOpenEncounter(encounter)
        return
      }
      applyInactiveEncounter(encounter, state)
    },
    [applyOpenEncounter, applyInactiveEncounter]
  )

  useEffect(() => {
    latestSequence.current = 0
    loadedInteractionIdRef.current = null
    setCurrentEncounterId(null)
    setCurrentEncounterSequenceNumber(null)
    setCurrentEncounterStatus(null)
    setEncounterState(null)
  }, [interactionId])

  const fetchCurrentEncounter = useCallback(async ({ silent = false } = {}) => {
    if (!interactionId) {
      setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    try {
      const result = await dataService.IntakeQuestions.getCurrentEncounter({ interactionId })
      applyEncounter(result?.data)
    } catch (err) {
      logger.error(`Failed to fetch current encounter for interaction ${interactionId}`, err)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [interactionId, applyEncounter])

  useEffect(() => {
    if (!interactionId) {
      setLoading(false)
      return undefined
    }

    const silent = loadedInteractionIdRef.current === interactionId
    fetchCurrentEncounter({ silent })
    loadedInteractionIdRef.current = interactionId

    if (!multiPatientEnabled) {
      return undefined
    }

    const onEncounter = (encounter) => {
      if (!toEncounterState(encounter)) {
        return
      }
      applyEncounter(encounter)
      setLoading(false)
    }

    const onError = (err) => {
      logger.error(`Encounter subscription failed for interaction ${interactionId}`, err)
      fetchCurrentEncounter({ silent: true })
    }

    const unsubscribe = dataService.IntakeQuestions.subscribeCurrentEncounter(onEncounter, {
      interactionId,
      onError,
    })

    return () => {
      if (Array.isArray(unsubscribe)) {
        unsubscribe.forEach((fn) => fn?.())
        return
      }
      unsubscribe?.()
    }
  }, [interactionId, multiPatientEnabled, fetchCurrentEncounter, applyEncounter])

  const canStartNextPatient = Boolean(multiPatientEnabled && startNextPatientEnabled)

  const startNextPatient = useCallback(async () => {
    if (!canStartNextPatient || !interactionId) return null
    setLoading(true)
    try {
      const result = await dataService.IntakeQuestions.advanceEncounter({ interactionId })
      applyEncounter(result?.data)
      return result?.data || null
    } catch (err) {
      logger.error(`Failed to advance encounter for interaction ${interactionId}`, err)
      return null
    } finally {
      setLoading(false)
    }
  }, [canStartNextPatient, interactionId, applyEncounter])

  const isPastFirstEncounter = Boolean(currentEncounterSequenceNumber > 1)

  useEffect(() => {
    const intervalMs = Number(refreshIntervalSeconds) * 1000
    if (!interactionId || !multiPatientEnabled || !Number.isFinite(intervalMs) || intervalMs <= 0) {
      return undefined
    }
    const timer = setInterval(() => fetchCurrentEncounter({ silent: true }), intervalMs)
    return () => clearInterval(timer)
  }, [interactionId, multiPatientEnabled, refreshIntervalSeconds, fetchCurrentEncounter])

  const isEncounterFinalized = currentEncounterStatus === FINALIZED

  const refreshedForFinalized = useRef(null)
  useEffect(() => {
    if (!isEncounterFinalized || !currentEncounterId) return
    if (refreshedForFinalized.current === currentEncounterId) return
    refreshedForFinalized.current = currentEncounterId
    logger.debug(`Encounter ${currentEncounterId} is finalized -- resyncing`)
    fetchCurrentEncounter({ silent: true })
  }, [isEncounterFinalized, currentEncounterId, fetchCurrentEncounter])

  const hasOpenEncounter = encounterState === OPEN && Boolean(currentEncounterId)

  return {
    currentEncounterId,
    currentEncounterSequenceNumber,
    currentEncounterStatus,
    encounterState,
    hasOpenEncounter,
    isEncounterFinalized,
    isPastFirstEncounter,
    canStartNextPatient,
    startNextPatient,
    loading,
    refetchEncounter: fetchCurrentEncounter,
  }
}
